
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated, useGetKnowledgeTagsBreakdown, useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, CheckCircle, ChevronDown, ChevronRight, Clock, PenSquare, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React, { useMemo, useState } from "react";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { TagsManagementView } from "./components/TagsManagementView";
import { KnowledgeCommonBlockCard } from "./components/KnowledgeCommonBlockCard";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "@/lib/permissions/stytch_original";
import { hasAccessToFeature } from "@/lib/models/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KnowledgeSearchComponent } from "./components/KnowledgeSearchComponent";

export function KnowledgeAuditReports() {
  const { toast } = useToast();
  const [link, setLink] = useState<string | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);
  const createReport = useCreateKnowledgeReport();

  const onGenerateReport = async () => {
    try {
      if (isGenerating) { return; }
      setIsGenerating(true);

      const result = await createReport();
      if (!result.success) {
        throw result;
      }

      setLink(result.data.link);
      downloadFromS3(result.data.link, "knowledge-audit-report.xlsx");

      toast({
        title: "Generated",
        description: "",
      });
    } catch (error) {
      setLink(undefined);
      console.error("Failed to generate knowledge item report", error);
      toast({
        title: "Failed to generate report",
        description: "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="font-medium">Knowledge Audit Reports</h3>
        <p className="text-sm text-muted-foreground">
          Generate a report of all knowledge items in the system, that <span className="italic">you</span> have access to.
        </p>

      </div>
      <div className="flex flex-col gap-4 md:grid md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>
              List Tasks Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={onGenerateReport} disabled={isGenerating}>
              {isGenerating ? "Generating..." : "Generate Report"}
            </Button>
            {link && !isGenerating && (
              <div className="flex flex-col gap-1 my-2">
                <Separator className="my-2" />
                <p className="text-sm text-muted-foreground">
                  Report generated successfully.
                </p>
                <a href={link} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-muted-foreground">Click here to view report, if it doesn't download automatically.</a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
